import {Owner} from '../../../owners/models/owner.model';
import {VehicleStorage} from '../../../storage/model/storage.model';
import {DateTime} from 'luxon';
import {Property} from '../../../property/model/property.model';
import {AvailablePropertyType} from '../../../property/enum/available-property.type';
import {AvailableVehicleCategory} from '../../../category/enum/available-vehicle-category.enum';

export class Vehicle {
  constructor(
    public readonly id: number,
    public readonly categoryName: string,
    public readonly owner: Owner,
    public readonly storage: VehicleStorage,
    public readonly archiveNumber: string,
    public readonly documentNumber: string,
    public readonly keyNumber: string,
    public readonly returnDate: DateTime | null,
    public readonly returningPerson: string,
    public readonly releaseDate: DateTime | null,
    public readonly receiver: string,
    public readonly properties: Property[]
  ) {
  }

  get noData(): string {
    return "Brak danych";
  }

  get displayName(): string {
    const brand = this.properties.find(property => property.propertyType === AvailablePropertyType.BRAND);
    const model = this.properties.find(property => property.propertyType === AvailablePropertyType.MODEL);

    return `${brand?.value} ${model?.value}`;
  }

  get displayBrand(): string {
    return this.properties.find(property => property.propertyType === AvailablePropertyType.BRAND)?.value ?? this.noData;
  }

  get displayModel(): string {
    return this.properties.find(property => property.propertyType === AvailablePropertyType.MODEL)?.value ?? this.noData;
  }

  get displayKeyOrDocumentNumber(): string {
    return this.keyNumber || this.documentNumber;
  }

  get displayFactoryNumber(): string {
    return this.properties.find(property => property.propertyType === AvailablePropertyType.FACTORY_NUMBER)?.value ?? this.noData;
  }

  get technicalInspectionDate(): string {
    const value = this.properties.find(property => property.propertyType === AvailablePropertyType.TECHNICAL_INSPECTION_DATE)?.value;
    if (value === undefined) return this.noData;

    return DateTime.fromISO(value).toFormat('dd.MM.yyyy');
  }

  get mileage(): string {
    return this.properties.find(property => property.propertyType === AvailablePropertyType.MILEAGE)?.value ?? this.noData;
  }

  get motoHours(): string {
    return this.properties.find(property => property.propertyType === AvailablePropertyType.MOTO_HOURS)?.value ?? this.noData;
  }

  get hasMileage(): boolean {
    return this.properties.find(property => property.propertyType === AvailablePropertyType.MILEAGE)?.value !== undefined;
  }

  get plateNumber(): string {
    return this.properties.find(property => property.propertyType === AvailablePropertyType.REGISTRATION_PLATE)?.value ?? this.noData;
  }

  get vin(): string {
    return this.properties.find(property => property.propertyType === AvailablePropertyType.VIN)?.value ?? this.noData;
  }

  get registrationCard(): string {
    return this.properties.find(property => property.propertyType === AvailablePropertyType.NUMBER_AND_SERIES_REGISTRATION_CARD)?.value ?? this.noData;
  }

  get insuranceExpiryDate(): string {
    const value = this.properties.find(property => property.propertyType === AvailablePropertyType.INSURANCE_EXPIRY_DATE)?.value;
    if (value === undefined) return this.noData;

    return DateTime.fromISO(value).toFormat('dd.MM.yyyy');
  }

  get annotations(): string {
    return this.properties.find(property => property.propertyType === AvailablePropertyType.ANNOTATIONS)?.value ?? '';
  }

  get hasAnnotations(): boolean {
    return this.annotations !== null;
  }

  get hasInsurance(): boolean {
    return this.properties.find(property => property.propertyType === AvailablePropertyType.INSURANCE_POLICY)?.value === 'true';
  }

  get displayKeyNumber(): string {
    return this.keyNumber ? this.keyNumber : 'Brak';
  }

  get displayDocumentNumber(): string {
    return this.documentNumber ? this.documentNumber : 'Brak';
  }

  get resolveCategoryIcon(): string {
    switch (this.categoryName) {
      case AvailableVehicleCategory.CAR:
        return 'tablerCar';
      case AvailableVehicleCategory.TRUCK:
        return 'tablerTruck';
      case AvailableVehicleCategory.TRAILERS:
        return 'lucideCaravan';
      case AvailableVehicleCategory.BUS:
        return 'tablerBus';
      case AvailableVehicleCategory.AGRICULTURAL_VEHICLE:
        return 'tablerTractor';
      case AvailableVehicleCategory.CONSTRUCTION_VEHICLE:
        return 'tablerCarCrane';
      case AvailableVehicleCategory.MOTORCYCLE:
        return 'tablerMotorbike';
      default:
        return 'tablerQuestionMark';
    }
  }

  get statusLabel(): string {
    if (this.isCompleted) return 'Wydany';
    if (this.isInStorage) return 'Magazyn';
    return 'Nieznany';
  }

  get statusColor(): string {
    if (this.isCompleted) return 'bg-green-500/25 text-green-600';
    if (this.isInStorage) return 'bg-primary-500/25 text-primary-600';
    if (this.isPending) return 'bg-orange-500/25 text-orange-600';
    return 'bg-gray-500/25 text-gray-600';
  }

  get isCompleted(): boolean {
    return this.wasReturned && this.wasReleased;
  }

  get isInStorage(): boolean {
    return this.returnDate !== null && this.releaseDate === null;
  }

  get wasReturned(): boolean {
    return this.returnDate !== null;
  }

  get wasReleased(): boolean {
    return this.releaseDate !== null;
  }

  get isPending(): boolean {
    return !this.wasReturned;
  }
}
